// Chat thread persistence for the AI Agent — per user, in Supabase.
// Each admin sees only their own history (RLS-scoped), synced across devices.
// The UI stays snappy: local state updates immediately and writes are debounced
// to the DB in the background.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatMessage, ChatThread } from './chatTypes';
import { supabase } from './supabase';

const MAX_THREADS = 50;
const PERSIST_ROW_CAP = 50; // cap persisted result rows so the jsonb stays small
const SAVE_DEBOUNCE_MS = 700;

const now = () => Date.now();
const genId = (): string =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `${now().toString(16)}-0000-4000-8000-${Math.random().toString(16).slice(2, 14)}`;

// Derive a short title from the first user message.
export const deriveTitle = (messages: ChatMessage[]): string => {
  const firstUser = messages.find((m) => m.role === 'user');
  const text = (firstUser?.content || '').trim().replace(/\s+/g, ' ');
  return text ? text.slice(0, 48) : '';
};

// Trim transient/heavy fields before persisting.
const slimMessages = (messages: ChatMessage[]): ChatMessage[] =>
  messages.map((m) => ({
    ...m,
    runningSql: undefined,
    results: m.results?.map((r) => ({ ...r, rows: r.rows.slice(0, PERSIST_ROW_CAP) })),
    attachments: m.attachments?.map((a) => ({ ...a, dataUrl: a.kind === 'image' ? undefined : a.dataUrl })),
  }));

export interface UseChatThreads {
  threads: ChatThread[];
  activeId: string | null;
  activeMessages: ChatMessage[];
  loading: boolean;
  newThread: () => string;
  selectThread: (id: string) => void;
  deleteThread: (id: string) => void;
  renameThread: (id: string, title: string) => void;
  clearActive: () => void;
  setActiveMessages: (messages: ChatMessage[]) => void;
}

export const useChatThreads = (): UseChatThreads => {
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const userIdRef = useRef<string | null>(null);
  const persistedIds = useRef<Set<string>>(new Set());
  const pending = useRef<Set<string>>(new Set());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const threadsRef = useRef<ChatThread[]>([]);
  threadsRef.current = threads;

  // Persist all dirty threads to the DB (upsert non-empty, delete empty/removed).
  const flush = useCallback(async () => {
    const uid = userIdRef.current;
    if (!uid) return;
    const ids = Array.from(pending.current);
    pending.current.clear();
    for (const id of ids) {
      const t = threadsRef.current.find((x) => x.id === id);
      const isEmpty = !t || t.messages.length === 0;
      if (isEmpty) {
        if (persistedIds.current.has(id)) {
          await supabase.from('agent_chats').delete().eq('id', id);
          persistedIds.current.delete(id);
        }
        continue;
      }
      const { error } = await supabase.from('agent_chats').upsert({
        id: t!.id,
        user_id: uid,
        title: t!.title || deriveTitle(t!.messages),
        messages: slimMessages(t!.messages),
        updated_at: new Date(t!.updatedAt).toISOString(),
      });
      if (!error) persistedIds.current.add(t!.id);
    }
  }, []);

  const markDirty = useCallback((id: string) => {
    pending.current.add(id);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void flush(); }, SAVE_DEBOUNCE_MS);
  }, [flush]);

  // Initial load from the DB for the signed-in user.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }
        userIdRef.current = user.id;
        const { data } = await supabase
          .from('agent_chats')
          .select('id,title,messages,created_at,updated_at')
          .order('updated_at', { ascending: false })
          .limit(MAX_THREADS);
        if (cancelled) return;
        const loaded: ChatThread[] = (data || []).map((r: any) => ({
          id: r.id,
          title: r.title || '',
          messages: Array.isArray(r.messages) ? r.messages : [],
          createdAt: new Date(r.created_at).getTime(),
          updatedAt: new Date(r.updated_at).getTime(),
        }));
        loaded.forEach((t) => persistedIds.current.add(t.id));
        setThreads(loaded);
        setActiveId(loaded[0]?.id ?? null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (timer.current) { clearTimeout(timer.current); void flush(); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeMessages = threads.find((t) => t.id === activeId)?.messages ?? [];

  const newThread = useCallback((): string => {
    const id = genId();
    const thread: ChatThread = { id, title: '', messages: [], createdAt: now(), updatedAt: now() };
    setThreads((prev) => [thread, ...prev]);
    setActiveId(id);
    return id;
  }, []);

  const selectThread = useCallback((id: string) => setActiveId(id), []);

  const deleteThread = useCallback((id: string) => {
    setThreads((prev) => {
      const next = prev.filter((t) => t.id !== id);
      setActiveId((cur) => (cur === id ? next[0]?.id ?? null : cur));
      return next;
    });
    markDirty(id); // flush deletes it from the DB (thread no longer present)
  }, [markDirty]);

  const renameThread = useCallback((id: string, title: string) => {
    setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, title: title.trim(), updatedAt: now() } : t)));
    markDirty(id);
  }, [markDirty]);

  const clearActive = useCallback(() => {
    setActiveId((cur) => {
      if (cur) {
        setThreads((prev) => prev.map((t) => (t.id === cur ? { ...t, messages: [], title: '', updatedAt: now() } : t)));
        markDirty(cur);
      }
      return cur;
    });
  }, [markDirty]);

  const setActiveMessages = useCallback((messages: ChatMessage[]) => {
    setThreads((prev) => {
      let id = activeId;
      if (!id || !prev.some((t) => t.id === id)) {
        id = genId();
        const thread: ChatThread = { id, title: deriveTitle(messages), messages, createdAt: now(), updatedAt: now() };
        setActiveId(id);
        pending.current.add(id);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => { void flush(); }, SAVE_DEBOUNCE_MS);
        return [thread, ...prev];
      }
      pending.current.add(id);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => { void flush(); }, SAVE_DEBOUNCE_MS);
      return prev.map((t) => (t.id === id ? { ...t, messages, title: t.title || deriveTitle(messages), updatedAt: now() } : t));
    });
  }, [activeId, flush]);

  return {
    threads,
    activeId,
    activeMessages,
    loading,
    newThread,
    selectThread,
    deleteThread,
    renameThread,
    clearActive,
    setActiveMessages,
  };
};
